import React, { useContext, useState, useEffect, useCallback, createContext } from 'react';
import OrganizationService from '../../../services/organization.service';

export interface SubscriptionContextType {
  subscriptionStatus: string | null;
  organizationName: string | null;
  setSubscriptionData: (status: string | null, orgName?: string | null) => void;
  clearPastDue: () => void;
  isPastDue: boolean;
  isLoaded: boolean;
  recheckSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider = ({ children }: { children: React.ReactNode }) => {
  // Hydrate from localStorage so state survives page refreshes
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(
    () => localStorage.getItem('subscriptionStatus')
  );
  const [organizationName, setOrganizationName] = useState<string | null>(
    () => localStorage.getItem('organizationName')
  );
  const [isLoaded, setIsLoaded] = useState(false);

  const isPastDue = subscriptionStatus === 'past_due';

  const setSubscriptionData = useCallback((status: string | null, orgName?: string | null) => {
    setSubscriptionStatus(status);
    if (status) {
      localStorage.setItem('subscriptionStatus', status);
    } else {
      localStorage.removeItem('subscriptionStatus');
    }

    if (orgName !== undefined) {
      setOrganizationName(orgName);
      if (orgName) {
        localStorage.setItem('organizationName', orgName);
      } else {
        localStorage.removeItem('organizationName');
      }
    }
  }, []);

  const clearPastDue = useCallback(() => {
    setSubscriptionStatus(null);
    setOrganizationName(null);
    localStorage.removeItem('subscriptionStatus');
    localStorage.removeItem('organizationName');
    localStorage.removeItem('pastDueFlag');
  }, []);

  // Re-verify subscription status from the API whenever an access token exists
  const recheckSubscription = useCallback(async () => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setIsLoaded(true);
      return;
    }

    try {
      const orgDetails = await OrganizationService.getOrganizationDetails();
      if (orgDetails?.subscriptionStatus === 'past_due') {
        setSubscriptionData('past_due', orgDetails.name || null);
        localStorage.setItem('pastDueFlag', 'true');
      } else {
        // Subscription is no longer past_due — clear any stale flags
        clearPastDue();
      }
    } catch (error) {
      console.error('Failed to verify subscription status:', error);
      // Keep whatever localStorage already says so we don't accidentally unlock
    } finally {
      setIsLoaded(true);
    }
  }, [setSubscriptionData, clearPastDue]);

  // On first mount, re-verify against the API (token may already be in localStorage)
  useEffect(() => {
    recheckSubscription();
  }, [recheckSubscription]);

  const value: SubscriptionContextType = {
    subscriptionStatus,
    organizationName,
    setSubscriptionData,
    clearPastDue,
    isPastDue,
    isLoaded,
    recheckSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
